import React, { useState, useRef, useEffect, KeyboardEvent } from "react";
import { View, TextInput, Pressable } from "react-native-web";
import { useNavigate } from "react-router-dom";
import CustomText from "../components/atoms/CustomText";
import Button from "../components/common/Button";
import { colors, spacing, } from "../utils/styling";
import { useSignInWithPassword } from "../lib/auth";
import { useUpdateQuizWithProfile } from "../lib/quizzes";
import { useAttachExercisesToProfile } from "../lib/exercises";
import { getResultsDataFromStateOrStorage } from "../utils/resultsData";
import { useResponsive } from "../hooks/useResponsive";
import SvgArrow from "../components/icons/SvgArrow";
import SvgEye from "../components/icons/SvgEye";

// McKayla 2026-06-18 (ClickUp 86badfkpb): a single login on the main site that
// branches by role on click — "Financial Advisors" vs "Individuals" — NOT a separate
// advisor subdomain. The split is a UX entry + post-login routing concern: both roles
// authenticate through the same Supabase auth. Advisors land in /advisor (which is
// itself gated on having an advisor row, backend require_advisor); individuals land in
// /client. A regular individual who picks "Financial Advisors" still can't reach the
// portal — the backend 403s them — so the choice is guidance, not a security boundary.
type LoginRole = "advisor" | "individual";

export default function Login() {
  const navigate = useNavigate();
  const [role, setRole] = useState<LoginRole | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Get the signin mutation
  const { mutateAsync: signIn } = useSignInWithPassword();
  const { mutateAsync: updateQuizWithProfile } = useUpdateQuizWithProfile();
  const { mutateAsync: attachExercisesToProfile } = useAttachExercisesToProfile();

  // Someone who retakes the quiz and then LOGS IN (instead of signing up) used to
  // land in their portal without the new result — only signup linked the quiz
  // (Nina hit this on the 9/23 call). Link it here when the quiz was taken under
  // the same email. The backend enforces the same rule and refuses anything else.
  const linkRetakenQuiz = async (loginEmail: string, profileId?: string) => {
    const results = getResultsDataFromStateOrStorage(null);
    const quizId = results?.quizId;
    const quizEmail = (results?.email || "").trim().toLowerCase();
    if (!quizId || !profileId || quizEmail !== loginEmail.trim().toLowerCase()) return;
    try {
      await updateQuizWithProfile({ quizId, profile: profileId });
      await attachExercisesToProfile({ quizId, profileId });
    } catch (e) {
      // Never block login on this; the quiz may already belong to this account.
      console.error("⚠️ Could not link retaken quiz to profile:", e);
    }
  };

  const { isMobile } = useResponsive();
  // Customizable logo size for mobile
  const mobileLogoSize = 108; // Change this value to your desired px height

  useEffect(() => {
    if (role) emailInputRef.current?.focus();
  }, [role]);

  const handleLogin = async () => {
    // Clear any previous errors
    setError("");

    // Validate inputs
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoggingIn(true);

    try {
      // Attempt to sign in - now returns both session and profile
      const { profile } = await signIn({
        email,
        password,
      });

      await linkRetakenQuiz(email, profile?.id);

      // Route by the chosen role first (the advisor portal is gated server-side, so
      // a non-advisor who picked "Financial Advisors" just bounces back), falling
      // back to user_type when role wasn't selected.
      // The advisor portal requires two-factor (compliance); /mfa enrolls or verifies,
      // then continues to the portal. The backend refuses staff data without it.
      const toPortal = () => navigate("/mfa", { state: { next: "/advisor" } });
      if (role === "advisor") {
        toPortal();
      } else if (role === "individual") {
        navigate("/client");
      } else if (profile.user_type === "client") {
        navigate("/client");
      } else if (["admin", "general"].includes(profile.user_type)) {
        toPortal();
      } else {
        console.warn("Unexpected user type:", profile.user_type);
        toPortal();
      }
    } catch (err) {
      // Handle login error
      setError(
        err instanceof Error ? err.message : "Invalid email or password"
      );
      setIsLoggingIn(false);
    }
  };

  // Step 1: role chooser. Clicking "Log in" lands here first (McKayla 6/18).
  if (!role) {
    const RoleButton = ({ value, title, subtitle }: { value: LoginRole; title: string; subtitle: string }) => (
      <Pressable
        onPress={() => { setError(""); setRole(value); }}
        style={({ hovered }: { hovered: boolean }) => ({
          width: "100%",
          maxWidth: 420,
          backgroundColor: hovered ? colors.gray[100] : colors.white,
          borderWidth: 1,
          borderColor: hovered ? colors.primary : colors.gray[200],
          borderRadius: 12,
          padding: spacing.lg,
          marginBottom: spacing.md,
          transition: "0.15s all ease-in",
          cursor: "pointer",
        })}
      >
        <CustomText style={{ fontFamily: "Nunito", fontSize: 20, fontWeight: "600", color: colors.dark, marginBottom: 4 }}>
          {title}
        </CustomText>
        <CustomText style={{ fontFamily: "Nunito", fontSize: 14, color: colors.gray[600] }}>
          {subtitle}
        </CustomText>
      </Pressable>
    );
    return (
      <View style={{ flex: 1, minHeight: "100vh", alignItems: "center", justifyContent: "center", backgroundColor: colors.gray[100], padding: spacing.lg }}>
        <View style={{ alignItems: "center", marginBottom: spacing.xl }}>
          <img
            src="/transparent-logo.png"
            alt="Money Personality Logo"
            style={{ height: 72, width: "auto", objectFit: "contain", cursor: "pointer", marginBottom: spacing.lg }}
            onClick={() => navigate("/")}
          />
          <CustomText style={{ fontFamily: "Nunito", fontSize: isMobile ? 28 : 40, fontWeight: "400", color: colors.dark, textAlign: "center" }}>
            Log in
          </CustomText>
          <CustomText style={{ fontFamily: "Nunito", fontSize: 16, color: colors.gray[600], textAlign: "center", marginTop: spacing.xs }}>
            How would you like to sign in?
          </CustomText>
        </View>
        <RoleButton value="advisor" title="Financial Advisors" subtitle="Access your firm's advisor portal." />
        <RoleButton value="individual" title="Individuals" subtitle="View your Money Personality results and workbook." />
        <Pressable onPress={() => navigate("/")} style={{ marginTop: spacing.md }}>
          <CustomText style={{ fontFamily: "Nunito", fontSize: 14, color: colors.primary, textDecorationLine: "underline" }}>
            Back to home
          </CustomText>
        </Pressable>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isMobile ? "column" : "row",
        minHeight: "100vh",
      }}
    >
      {/* Left Side - Login Form */}
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flex: isMobile ? undefined : 0.6,
        }}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: isMobile ? undefined : 0.6,
            width: isMobile ? "100%" : undefined,
            backgroundColor: colors.gray[100],
            padding: isMobile ? spacing.lg : spacing.xxl,
            boxShadow: isMobile
              ? undefined
              : "inset -1px 0 0 0 rgba(0, 0, 0, 0.05), inset -2px 0 0 0 rgba(0, 0, 0, 0.03)",
          }}
        >
          {/* Back to Home button */}
          {isMobile && (
            <View style={{
              alignSelf: 'flex-start',
              marginBottom: isMobile ? spacing.xl : spacing.xxl,
            }}>
              <Pressable 
                style={({ hovered }: { hovered: boolean}) => ({
                  backgroundColor: hovered ? colors.gray[600] : 'black',
                  transition: '0.2s all ease-in',
                  borderRadius: 16,
                  maxWidth: isMobile ? 100 : 200,
                  paddingHorizontal: isMobile ? spacing.md : spacing.md,
                  paddingVertical: isMobile ? spacing.xs : spacing.sm,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: isMobile ? 5 : 10
                })}
                onPress={() => navigate("/")}
              >
                <SvgArrow direction="left" color="white" width={isMobile ? 15 : 20} height={isMobile ? 15 : 20} />
                <CustomText style={{
                  fontFamily: "Nunito",
                  color: 'white',
                  fontSize: isMobile ? 14 : 18,
                }}>
                  Home
                </CustomText>
              </Pressable>
            </View>
          )}

          {/* Form Content Container */}
          <View style={{
            flex: 1,
            display: 'flex',
            alignItems: "center",
            justifyContent: "center",
          }}>
            {/* Logo on mobile */}
            {isMobile && (
              <View style={{ alignSelf: "center" }}>
                <img
                  src="/transparent-logo.png"
                  alt="Money Personality Logo"
                  style={{
                    height: mobileLogoSize,
                    width: "auto",
                    cursor: "pointer",
                    objectFit: "contain",
                    padding: 24,
                  }}
                  onClick={() => navigate("/")}
                />
              </View>
            )}
            {/* Form Content */}
            <View style={{ 
              width: "100%", 
              maxWidth: isMobile ? 340 : 600,
            }}>
              <CustomText
                style={{
                  fontSize: isMobile ? 30 : 60,
                  fontWeight: "400",
                  marginBottom: isMobile ? spacing.xs : spacing.sm,
                  fontFamily: "Nunito",
                  textAlign: isMobile ? "center" : undefined,
                }}
              >
                Welcome back!
              </CustomText>
              <CustomText
                style={{
                  fontSize: isMobile ? 18 : 24,
                  color: colors.gray[600],
                  marginBottom: spacing.xs,
                  fontFamily: "Nunito",
                  textAlign: isMobile ? "center" : undefined,
                }}
              >
                {role === "advisor" ? "Financial Advisor sign-in." : "Log in to your account below."}
              </CustomText>
              <Pressable
                onPress={() => { setError(""); setRole(null); }}
                style={{ marginBottom: isMobile ? spacing.md : spacing.lg, alignSelf: isMobile ? "center" : "flex-start" }}
              >
                <CustomText style={{ fontFamily: "Nunito", fontSize: 13, color: colors.primary, textDecorationLine: "underline" }}>
                  Not a {role === "advisor" ? "financial advisor" : "individual"}? Switch
                </CustomText>
              </Pressable>

              {/* Account Information Section */}
              <View style={{ marginBottom: spacing.xl }}>
                <View style={{ marginBottom: spacing.md }}>
                  <CustomText
                    style={{
                      marginBottom: spacing.sm,
                      color: colors.dark,
                      fontFamily: "Nunito",
                      fontSize: 12,
                    }}
                  >
                    Email
                  </CustomText>
                  <TextInput
                    ref={emailInputRef}
                    value={email}
                    onChangeText={setEmail}
                    placeholder="john@example.com"
                    placeholderTextColor={colors.gray[500]}
                    style={{
                      height: 40,
                      borderWidth: 1,
                      borderColor: colors.gray[200],
                      borderRadius: 6,
                      paddingHorizontal: 12,
                      outlineStyle: "none",
                      width: "100%",
                      fontFamily: "Nunito",
                      fontSize: 14,
                      backgroundColor: colors.white,
                    }}
                  />
                </View>

                <View style={{ marginBottom: spacing.md }}>
                  <CustomText
                    style={{
                      marginBottom: spacing.sm,
                      color: colors.dark,
                      fontFamily: "Nunito",
                      fontSize: 12,
                    }}
                  >
                    Password
                  </CustomText>
                  <View style={{
                    position: "relative",
                    width: "100%",
                  }}>
                    <TextInput
                      value={password}
                      onChangeText={setPassword}
                      placeholder="********"
                      placeholderTextColor={colors.gray[500]}
                      secureTextEntry={!showPassword}
                      onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (
                          e.key === "Enter" &&
                          !(!email || !password || isLoggingIn)
                        ) {
                          handleLogin();
                        }
                      }}
                      style={{
                        height: 40,
                        borderWidth: 1,
                        borderColor: colors.gray[200],
                        borderRadius: 6,
                        paddingHorizontal: 12,
                        paddingRight: 40,
                        outlineStyle: "none",
                        width: "100%",
                        fontFamily: "Nunito",
                        fontSize: 14,
                        backgroundColor: colors.white,
                      }}
                    />
                    <Pressable
                      onPress={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: 12,
                        top: 7,
                        padding: 4,
                      }}
                    >
                      <SvgEye
                        width={20}
                        height={20}
                        color={colors.gray[500]}
                        isOpen={showPassword}
                      />
                    </Pressable>
                  </View>
                  <View style={{ marginTop: spacing.sm, alignItems: "flex-end" }}>
                    <Pressable onPress={() => navigate("/forgot-password")}>
                      <CustomText
                        style={{
                          fontSize: 12,
                          color: colors.primary,
                          fontFamily: "Nunito",
                          textDecorationLine: "underline",
                        }}
                      >
                        Forgot Password?
                      </CustomText>
                    </Pressable>
                  </View>
                </View>
              </View>

              {error && (
                <CustomText
                  style={{
                    color: colors.danger,
                    marginTop: spacing.sm,
                    fontSize: 14,
                    fontFamily: "Nunito",
                    marginBottom: 10,
                    paddingLeft: 4,
                  }}
                >
                  {error}
                </CustomText>
              )}

              <View style={{ marginBottom: spacing.xl }}>
                <Button
                  onPress={handleLogin}
                  label="Log In"
                  loading={isLoggingIn}
                  disabled={!email || !password || isLoggingIn}
                  rounded={false}
                  style={{
                    width: "100%",
                    height: 40,
                  }}
                  textStyle={{
                    fontSize: 14,
                  }}
                />
              </View>
              {!isMobile && (
                <View style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginBottom: isMobile ? spacing.xl : spacing.xxl,
                }}>
                  <Pressable 
                    style={({ hovered }: { hovered: boolean}) => ({
                      backgroundColor: hovered ? colors.gray[600] : 'black',
                      transition: '0.2s all ease-in',
                      borderRadius: 16,
                      width: '50%',
                      paddingHorizontal: isMobile ? spacing.md : spacing.md,
                      paddingVertical: isMobile ? spacing.xs : spacing.sm,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: isMobile ? 5 : 10
                    })}
                    onPress={() => navigate("/")}
                  >
                    <SvgArrow direction="left" color="white" width={isMobile ? 15 : 20} height={isMobile ? 15 : 20} />
                    <CustomText style={{
                      fontFamily: "Nunito",
                      color: 'white',
                      fontSize: isMobile ? 14 : 18,
                    }}>
                      Home
                    </CustomText>
                  </Pressable>
                </View>
              )}
            </View>
          </View> {/* <-- Properly close the form content container */}
        </View>
      </View>

      {/* Right Side - Testimonial */}
      <View
        style={{
          flex: isMobile ? undefined : 0.4,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.white,
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: isMobile ? "center" : undefined,
        }}
      >
        {/* Logo only on desktop */}
        {!isMobile && (
          <View style={{ marginBottom: spacing.xxl, alignSelf: "flex-end" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: 60,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}

        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: isMobile ? spacing.lg : 100,
          }}
        >
          <View style={{ maxWidth: 420 }}>
            <CustomText
              style={{
                fontSize: 28,
                lineHeight: 42,
                color: colors.gray[900],
                marginBottom: spacing.xl,
                fontFamily: "Nunito",
                textAlign: isMobile ? "center" : undefined,
              }}
            >
              "I can't believe how accurate the Money Personality test is...it
              felt like someone had been watching my relationship with money my
              entire life."
            </CustomText>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: isMobile ? "center" : undefined,
              }}
            >
              <img
                src={require("../components/img/ben-pic.jpeg")}
                alt="Ben's profile"
                style={{
                  width: isMobile ? 75 : 100,
                  height: "auto",
                  borderRadius: 100,
                  objectFit: "cover",
                  marginRight: spacing.md,
                }}
              />
              <CustomText
                style={{
                  fontSize: 18,
                  color: colors.gray[600],
                  fontFamily: "Nunito",
                }}
              >
                Ben Barton, Entrepreneur
              </CustomText>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
